/**
 * transcriptItemLinker — find what was said about each item, word for word.
 *
 * Brief K slice 2b; registered capability `transcript_item_linker` (Doc 22 §4).
 * The model reads a session's transcript and the items it ranked, and proposes
 * passages where someone gave a reason about an item. This module is the part
 * that can be trusted without trusting the model:
 *
 *   - a proposal survives only if its words are found in the transcript; the
 *     excerpt kept is the transcript's own text, not the model's copy of it;
 *   - the timestamp is read from the transcript at that spot, never from the
 *     model;
 *   - only the session's own item ids are accepted.
 *
 * What survives is written as CANDIDATE annotations (record_transcript_links,
 * migration 831). A person confirms or rejects each one; nothing counts before.
 * Pure — no I/O — so the golden set in transcriptItemLinker.test.ts runs it.
 */

/** Characters of transcript per model call. */
export const LINKER_CHUNK_CHARS = 12_000;
/** Each part repeats the end of the one before, so a passage on a boundary is seen whole. */
export const LINKER_CHUNK_OVERLAP = 400;
/** A longer transcript is read in this many parts at most (about 90,000 characters). */
export const LINKER_MAX_CHUNKS = 8;
/** At most this many passages are proposed for one item in one run. */
export const LINKER_MAX_PER_ITEM = 3;
/** A quote shorter than this is a mention, not a reason. */
const MIN_QUOTE_CHARS = 20;
const MAX_QUOTE_CHARS = 600;

export interface LinkerItem {
  id: string;
  title: string;
}

export interface LinkerProposal {
  itemId: string;
  quote: string;
}

export interface VerifiedLink {
  itemId: string;
  /** The transcript's own words at the place the quote was found. */
  excerpt: string;
  /** The nearest timestamp written before that place, e.g. "12:03"; null when the transcript has none. */
  transcriptTimestamp: string | null;
}

export interface TranscriptChunk {
  start: number;
  text: string;
}

/** Split a long transcript into overlapping parts, cutting at a line break where one is near. */
export function chunkTranscript(
  text: string,
  size = LINKER_CHUNK_CHARS,
  overlap = LINKER_CHUNK_OVERLAP,
  maxChunks = LINKER_MAX_CHUNKS,
): TranscriptChunk[] {
  const chunks: TranscriptChunk[] = [];
  let start = 0;
  while (start < text.length && chunks.length < maxChunks) {
    let end = Math.min(text.length, start + size);
    if (end < text.length) {
      const lineBreak = text.lastIndexOf('\n', end);
      if (lineBreak > start + size / 2) end = lineBreak;
    }
    chunks.push({ start, text: text.slice(start, end) });
    if (end >= text.length) break;
    start = Math.max(end - overlap, start + 1);
  }
  return chunks;
}

const LINKER_RULES = `Rules:
- Copy the words exactly as they appear in the transcript: one to three sentences, no edits, no "...", no summary.
- Only a reason counts: why an item should rank higher or lower, what doing it or not doing it would cost, risk or win. A passing mention of an item does not count.
- Use only the item ids listed. If a passage is about none of them, leave it out.
- At most ${LINKER_MAX_PER_ITEM} passages per item. If nothing in this part is about an item, return an empty list.`;

export const LINKER_SYSTEM_PROMPT =
  'You find evidence in a meeting transcript. You copy the exact words people said; you never summarize, interpret, or add words. You answer in JSON only.';

export function buildLinkerPrompt(items: LinkerItem[], chunk: string): string {
  return `These are the items a prioritization session ranked:
${items.map((i) => `- id=${i.id} | ${i.title}`).join('\n')}

This is part of the session's transcript:
"""
${chunk}
"""

Find the passages where someone gives a reason about one of these items.

${LINKER_RULES}

Return JSON only:
{ "links": [ { "item_id": "<id from the list>", "quote": "<the exact words>" } ] }`;
}

/** Read the model's answer. Unknown ids and empty quotes are dropped; a broken answer is an empty list. */
export function parseLinkerResponse(raw: string | null, allowedItemIds: Iterable<string>): LinkerProposal[] {
  if (!raw) return [];
  const match = raw.match(/\{[\s\S]*\}/);
  if (!match) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(match[0]);
  } catch {
    return [];
  }
  const links = (parsed as { links?: unknown })?.links;
  if (!Array.isArray(links)) return [];
  const allowed = new Set(allowedItemIds);
  const out: LinkerProposal[] = [];
  for (const l of links) {
    const itemId = typeof (l as { item_id?: unknown })?.item_id === 'string' ? (l as { item_id: string }).item_id.trim() : '';
    const quote = typeof (l as { quote?: unknown })?.quote === 'string' ? (l as { quote: string }).quote.trim() : '';
    if (itemId && quote && allowed.has(itemId)) out.push({ itemId, quote });
  }
  return out;
}

const QUOTE_CHARS: Record<string, string> = {
  '‘': "'", '’': "'", '‚': "'", '‛': "'",
  '“': '"', '”': '"', '„': '"', '‟': '"',
  '–': '-', '—': '-', '−': '-', '…': '...',
};

/** Lower-case, plain quotes and dashes, single spaces — with a map back to the original positions. */
export function normalizeForMatch(text: string): { norm: string; map: number[] } {
  let norm = '';
  const map: number[] = [];
  let lastWasSpace = true;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (/\s/.test(ch)) {
      if (!lastWasSpace) {
        norm += ' ';
        map.push(i);
        lastWasSpace = true;
      }
      continue;
    }
    const plain = (QUOTE_CHARS[ch] ?? ch).toLowerCase();
    for (const c of plain) {
      norm += c;
      map.push(i);
    }
    lastWasSpace = false;
  }
  if (norm.endsWith(' ')) {
    norm = norm.slice(0, -1);
    map.pop();
  }
  return { norm, map };
}

const EDGE_PUNCTUATION = /^[\s"'.,;:!?-]+|[\s"'.,;:!?-]+$/g;

/** Where the quote's words are in the transcript, as original positions; null when they are not there. */
export function locateQuote(transcript: string, quote: string): { start: number; end: number } | null {
  const q = normalizeForMatch(quote).norm.replace(EDGE_PUNCTUATION, '');
  if (q.length < MIN_QUOTE_CHARS || q.split(' ').length < 4) return null;
  const { norm, map } = normalizeForMatch(transcript);
  const at = norm.indexOf(q);
  if (at < 0) return null;
  let end = map[at + q.length - 1] + 1;
  // Keep the sentence's own closing marks ("review." not "review").
  while (end < transcript.length && /[.!?'"’”)]/.test(transcript[end])) end++;
  return { start: map[at], end };
}

const STAMP_AT_LINE_START = /(?:^|\n)[ \t]*\[?(\d{1,2}:\d{2}(?::\d{2})?)\]?/g;

/** The last timestamp written at the start of a line before `index`, e.g. "[12:03]" → "12:03". */
export function timestampBefore(transcript: string, index: number): string | null {
  const lineEnd = transcript.indexOf('\n', index);
  const head = transcript.slice(0, lineEnd < 0 ? transcript.length : lineEnd);
  let found: string | null = null;
  for (const m of head.matchAll(STAMP_AT_LINE_START)) {
    const lineStart = (m.index ?? 0) + (m[0].startsWith('\n') ? 1 : 0);
    if (lineStart > index) break;
    found = m[1];
  }
  return found;
}

/** A leading "[12:03] Speaker:" belongs to the line, not to the words: drop it from the excerpt. */
function trimLinePrefix(excerpt: string): string {
  return excerpt.replace(/^[ \t]*\[?\d{1,2}:\d{2}(?::\d{2})?\]?[ \t]*/, '').trim();
}

/**
 * Keep only proposals whose words are in the transcript, for the session's
 * items, one copy of each, and at most LINKER_MAX_PER_ITEM per item.
 */
export function verifyProposals(transcript: string, proposals: LinkerProposal[], items: LinkerItem[]): VerifiedLink[] {
  const known = new Set(items.map((i) => i.id));
  const seen = new Set<string>();
  const perItem = new Map<string, number>();
  const out: VerifiedLink[] = [];
  for (const p of proposals) {
    if (!known.has(p.itemId)) continue;
    if ((perItem.get(p.itemId) ?? 0) >= LINKER_MAX_PER_ITEM) continue;
    const found = locateQuote(transcript, p.quote);
    if (!found) continue;
    const excerpt = trimLinePrefix(transcript.slice(found.start, found.end));
    if (excerpt.length < MIN_QUOTE_CHARS || excerpt.length > MAX_QUOTE_CHARS) continue;
    const key = `${p.itemId}\u0000${normalizeForMatch(excerpt).norm}`;
    if (seen.has(key)) continue;
    seen.add(key);
    perItem.set(p.itemId, (perItem.get(p.itemId) ?? 0) + 1);
    out.push({ itemId: p.itemId, excerpt, transcriptTimestamp: timestampBefore(transcript, found.start) });
  }
  return out;
}
